import { useUpdateEffect } from 'react-use';

import {
   HealthcareProviderAppointmentCancellationTokenInReminderEnabledQuery,
   HealthcareProviderAppointmentCancellationTokenInReminderEnabledSetCommand,
   HealthcareProviderAppointmentReminderPatientNotificationConfigurationQuery,
   HealthcareProviderNotificationConfiguration,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult, useCqrsGet } from '@dreryk/command-query-react-common';
import { Translate, useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   Form,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   StyledMargin,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleLoader } from './PatientPhoneNotificationAppointmentCancellationTokenInReminderToggle.loader';
import { StyledTooltipText } from './PatientPhoneNotificationTooltipText.styled';

const SNACKBAR_DELAY_IN_MS = 5000;

const useCurrentHealthcareProviderAppointmentCancellationTokenInReminderEnabledSetCommand = (): UseCqrsCommandResult<
   void,
   PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleFormBean
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentCancellationTokenInReminderEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (__, parameters) => {
            dispatchSnackbarPositiveEvent(
               parameters.notificationEnabled
                  ? patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentCancellationTokenInConfirmationToggleSaveOn
                  : patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentCancellationTokenInConfirmationToggleSaveOff,

               {
                  autoHideDelayInMs: SNACKBAR_DELAY_IN_MS,
               }
            );
            refetchCqrsQuery(HealthcareProviderAppointmentCancellationTokenInReminderEnabledQuery);
            refetchCqrsQuery(HealthcareProviderAppointmentReminderPatientNotificationConfigurationQuery);
         },
      }
   );
};

type PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleFormBean = {
   notificationEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const PatientPhoneNotificationAppointmentCancellationTokenInReminderToggle = (props: {
   readonly notificationEnabled: boolean;
}): JSX.Element | null => {
   const {
      register,
      handleSubmit,
      reset,
   } = useFormController<PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );

   useUpdateEffect(() => {
      reset({
         notificationEnabled: props.notificationEnabled,
      });
   }, [props.notificationEnabled]);

   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderAppointmentCancellationTokenInReminderEnabledSetCommand();

   const {
      data: healthcareProviderNotificationConfiguration,
   } = useCqrsGet<HealthcareProviderNotificationConfiguration>(() => {
      return new HealthcareProviderAppointmentReminderPatientNotificationConfigurationQuery();
   });

   return (
      <StyledMargin left={32}>
         <StyledFlex gap={'12px'}>
            <Form onInput={handleSubmit(onSubmit)}>
               <Toggle
                  controller={register('notificationEnabled')}
                  testId={'patientPhoneNotificationAppointmentCancellationTokenInReminderToggle'}
                  disabled={
                     loading ||
                     !healthcareProviderNotificationConfiguration ||
                     !healthcareProviderNotificationConfiguration.enabled
                  }
               />
            </Form>
            <StyledFlex gap={'4px'} alignItems={'center'}>
               <Translate>
                  {patientPhoneNotificationTranslationKeys.notificationToggle.appointmentCancellationToggle}
               </Translate>
               <Tooltip
                  maxWidth={'500px'}
                  testId={'appointmentCancellationTokenInReminderToggleTooltip'}
                  content={
                     <StyledTooltipText>
                        <Translate>
                           {
                              patientPhoneNotificationTranslationKeys.notificationToggle
                                 .appointmentCancellationTokenInReminderToggleTooltip
                           }
                        </Translate>
                     </StyledTooltipText>
                  }
               >
                  <IconWrapper
                     testId={'appointmentCancellationTokenInReminderToggleTooltipIconWrapper'}
                     fillColor={IconFillColor.PRIMARY}
                     size={IconSize.SMALL}
                  >
                     <QuestionmarkCircleIcon />
                  </IconWrapper>
               </Tooltip>
            </StyledFlex>
         </StyledFlex>
      </StyledMargin>
   );
};

export const PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleLoader>
         {({ notificationEnabled }) => (
            <PatientPhoneNotificationAppointmentCancellationTokenInReminderToggle
               notificationEnabled={notificationEnabled}
            />
         )}
      </PatientPhoneNotificationAppointmentCancellationTokenInReminderToggleLoader>
   );
};
